import {
  CALCULATION_CYCLE_HISTORY_PAGE_SIZE,
  CALCULATION_CYCLE_OVERLAP_CHECK_MAX_PAGES,
  CALCULATION_CYCLE_OVERLAP_CHECK_PAGE_SIZE,
} from "@/features/tuition/constants";
import type { CalculationCycle, CalculationCyclePage } from "@/features/tuition/types";
import {
  parseCalculationCyclePage,
  parseCurrentCalculationCycle,
  readApiJson,
} from "@/features/tuition/utils/parse-calculation-cycle";
import { toNumericId } from "@/features/tuition/utils/to-numeric-id";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils/api/api-client";
import { isNotFoundError } from "@/lib/utils/media/is-not-found-error";
import { CalculationType, TuitionApi } from "@/openapi/generated";

export function getCurrentCalculationCycleQueryKey() {
  return [QueryKeys.CalculationCycleCurrent, CalculationType.Tuition] as const;
}

export function getCalculationCyclesQueryKey(
  page = 1,
  pageSize = CALCULATION_CYCLE_HISTORY_PAGE_SIZE,
) {
  return [
    QueryKeys.CalculationCycles,
    CalculationType.Tuition,
    page,
    pageSize,
  ] as const;
}

export async function fetchCurrentCalculationCycle(
  accessToken: string,
  tenantId: number,
): Promise<CalculationCycle | null> {
  const service = createAuthenticatedApi(TuitionApi, accessToken);

  try {
    const response = await service.apiTuitionCalculationCyclesCurrentTypeGetRaw(
      {
        type: CalculationType.Tuition,
        xTenantID: tenantId,
      },
    );
    const cycle = parseCurrentCalculationCycle(await readApiJson(response));
    if (!cycle || cycle.isClosed || !toNumericId(cycle.id)) {
      return null;
    }
    return cycle;
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }
    throw error;
  }
}

export async function fetchCalculationCyclePage(
  accessToken: string,
  tenantId: number,
  page = 1,
  pageSize = CALCULATION_CYCLE_HISTORY_PAGE_SIZE,
): Promise<CalculationCyclePage> {
  const service = createAuthenticatedApi(TuitionApi, accessToken);
  const response = await service.apiTuitionCalculationCyclesHistoryPostRaw({
    getCalculationCycleRequest: {
      calculationType: CalculationType.Tuition,
      fromDate: null,
      toDate: null,
    },
    page,
    pageSize,
    xTenantID: tenantId,
  });
  return parseCalculationCyclePage(await readApiJson(response));
}

export async function fetchCalculationCyclesForOverlapCheck(
  accessToken: string,
  tenantId: number,
): Promise<CalculationCycle[]> {
  const items: CalculationCycle[] = [];

  for (let page = 1; page <= CALCULATION_CYCLE_OVERLAP_CHECK_MAX_PAGES; page++) {
    const result = await fetchCalculationCyclePage(
      accessToken,
      tenantId,
      page,
      CALCULATION_CYCLE_OVERLAP_CHECK_PAGE_SIZE,
    );
    items.push(...result.items);
    if (!result.hasNextPage) break;
  }

  return items;
}
